import React, { useEffect, useState } from "react";
import {
  Box,
  Typography
} from "@mui/material";
import { StyledCheckbox, StyledWhiteCheckbox } from "../../../components/commonComponents/StyledComponents/styled";
import { useSelector } from "react-redux";
import { ArrowDropDown, ArrowDropUp, Cancel, Check, Close } from "@mui/icons-material";
import { canViewStoreOwner } from "../../../api_calls/userManagement";
import {
  ListingCellLeft,
  ListingCellCenter,
  ListingHeaderContent,
  ListingIconMetric,
  ListingMetricValue,
} from "../../../components/commonComponents/TabularLists/styles";
import ListingDataTableForModal from "../../../components/commonComponents/TabularLists/ListingDataTableForModal";

const StoreDetailSelection = ({
  selectedStores,
  setSelectedStores,
  filteredStores,
  open,
  sortedList,
  setSortedList,
  slicedFilteredStores,
  setSlicedFilteredStores,
  setTotalList,
  statusBasedReadOnly = false
}) => {
  const currentStateData = useSelector(state => state.userSelections);
  // Sorting state
  const [sortBy, setSortBy] = useState("Store Name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Pagination values from Redux
  const start = currentStateData.store_pagination?.store_list_start ?? 0;
  const end = currentStateData.store_pagination?.store_list_end ?? 10;

  const showListedColumn = typeof currentStateData.listing_check_saved_for_campaign !== "undefined" ? currentStateData.listing_check_saved_for_campaign : false;

  // Same rule as the Owner filter in StoreDetailFilter, so column and filter stay in sync
  const showOwnerColumn = canViewStoreOwner();

  let tableColName = ["Store Name", "Street & No.", "ZIP Code", "City", "Region"];

  if (showOwnerColumn) {
    tableColName = [...tableColName, "Owner"];
  }

  if (showListedColumn) {
    tableColName = [...tableColName, "Listed"];
  }

  // const tableColName = showListedColumn ? ["Store Name", "Street & No.", "ZIP Code", "City", "Region", "Owner", "Listed"] : ["Store Name", "Street & No.", "ZIP Code", "City", "Region", "Owner"];

  /**
   * Sort helper – always returns a new array
   */
  const sortListBy = (list, sortKey, direction) => {
    const sorted = [...list];

    sorted.sort((a, b) => {
      let valueA;
      let valueB;

      switch (sortKey) {
        case "Store Name":
          valueA = a.name;
          valueB = b.name;
          break;

        case "Street & No.":
          valueA = a.address;
          valueB = b.address;
          break;

        case "ZIP Code":
          valueA = Number(a.zipCode) || 0;
          valueB = Number(b.zipCode) || 0;
          break;

        case "City":
          valueA = a.city;
          valueB = b.city;
          break;

        case "Region":
          valueA = a.region?.name ?? "";
          valueB = b.region?.name ?? "";
          break;

        case "Owner":
          valueA = a.company?.name ?? "";
          valueB = b.company?.name ?? "";
          break;
        case "Listed":
          valueA = a.listing_check_saved;
          valueB = b.listing_check_saved ?? "";
          break;

        default:
          return 0;
      }

      if (typeof valueA === "string") {
        return direction === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      if (typeof valueA === "number" || typeof valueA === "boolean") {
        return direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }

      return direction === "asc" ? valueA - valueB : valueB - valueA;
    });

    return sorted;
  };

  /**
   * Recalculate sorted list whenever filters or sort state change
   */
  useEffect(() => {
    const result = sortListBy(filteredStores, sortBy, sortDirection);
    setSortedList(result);
  }, [filteredStores, sortBy, sortDirection]);


  useEffect(() => {
    setSelectedStores(currentStateData.selected_store_options);
    setSlicedFilteredStores(sortedList.slice(start, end));
    setTotalList(currentStateData.stores_list || []);
  }, [open]);

  useEffect(() => {
    // Apply pagination AFTER sorting
    setSlicedFilteredStores(sortedList.slice(start, end));
  }, [sortedList, start, end]);

  /**
   * Handle column header click
   */
  const handleSortClick = (label) => {
    if (sortBy === label) {
      // Toggle sort direction when clicking same column
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      // New column → default to ascending
      setSortBy(label);
      setSortDirection("asc");
    }
  };

  /**
   * Single store selection handling
   */
  const updateSelection = (id, label, retailer_id, checked) => {
    // Prevent changes if read-only
    if (statusBasedReadOnly) return;

    setSelectedStores(prev => {
      if (checked) {
        return [...prev, { id, label, retailer_id }];
      }
      return prev.filter(s => s.id !== id);
    });
  };

  /**
   * Check if all visible stores are selected
   */
  const areAllStoresSelected = () => {
    if (!slicedFilteredStores.length) return false;
    return slicedFilteredStores.every(store =>
      selectedStores.some(s => s.id === store.id)
    );
  };

  /**
   * Select / deselect all visible stores
   */
  const toggleSelectAll = (checked) => {
    // Fix #15: Prevent changes if read-only
    if (statusBasedReadOnly) return;

    if (checked) {
      setSelectedStores(
        sortedList.map(store => ({
          id: store.id,
          label: store.name,
          retailer_id: store.retailer_id
        }))
      );
    } else {
      setSelectedStores([]);
    }
  };

  const sortArrow = (label) => {
    const isActive = sortBy === label;
    return isActive && sortDirection === "desc" ? (
      <ArrowDropUp />
    ) : <ArrowDropDown />;
  };

  // Define columns for the table, including conditional columns based on user type and listing check availability
  const columns = React.useMemo(() => {
    const cols = [
      {
        id: "select",
        headerAlign: "center",
        align: "center",
        header: (
          <ListingCellCenter>
            <StyledWhiteCheckbox
              size="small"
              checked={areAllStoresSelected()}
              onChange={(e) => toggleSelectAll(e.target.checked)}
              disabled={statusBasedReadOnly}
            />
          </ListingCellCenter>
        ),
        render: (store) => (
          <ListingCellCenter>
            <StyledCheckbox
              size="small"
              checked={selectedStores.some((s) => s.id === store.id)}
              onChange={(e) =>
                updateSelection(store.id, store.name, store.retailer_id, e.target.checked)
              }
              disabled={statusBasedReadOnly}
            />
          </ListingCellCenter>
        ),
      },

      {
        id: "name",
        header: (
          <ListingHeaderContent onClick={() => handleSortClick("Store Name")} style={{ cursor: "pointer" }}>
            Store Name {sortArrow("Store Name")}
          </ListingHeaderContent>
        ),
        render: (store) => <ListingCellLeft>{store.name}</ListingCellLeft>,
      },

      {
        id: "address",
        header: (
          <ListingHeaderContent onClick={() => handleSortClick("Street & No.")} style={{ cursor: "pointer" }}>
            Street &amp; No. {sortArrow("Street & No.")}
          </ListingHeaderContent>
        ),
        render: (store) => <ListingCellLeft>{store.address}</ListingCellLeft>,
      },

      {
        id: "zipCode",
        header: (
          <ListingHeaderContent onClick={() => handleSortClick("ZIP Code")} style={{ cursor: "pointer" }}>
            ZIP Code {sortArrow("ZIP Code")}
          </ListingHeaderContent>
        ),
        render: (store) => <ListingCellLeft>{store.zipCode}</ListingCellLeft>,
      },

      {
        id: "city",
        header: (
          <ListingHeaderContent onClick={() => handleSortClick("City")} style={{ cursor: "pointer" }}>
            City {sortArrow("City")}
          </ListingHeaderContent>
        ),
        render: (store) => <ListingCellLeft>{store.city}</ListingCellLeft>,
      },

      {
        id: "region",
        header: (
          <ListingHeaderContent onClick={() => handleSortClick("Region")} style={{ cursor: "pointer" }}>
            Region {sortArrow("Region")}
          </ListingHeaderContent>
        ),
        render: (store) => <ListingCellLeft>{store.region?.name ?? "-"}</ListingCellLeft>,
      },
    ];

    if (showOwnerColumn) {
      cols.push(
        {
          id: "owner",
          header: (
            <ListingHeaderContent onClick={() => handleSortClick("Owner")} style={{ cursor: "pointer" }}>
              Owner {sortArrow("Owner")}
            </ListingHeaderContent>
          ),
          render: (store) => <ListingCellLeft>{store.company?.name ?? "-"}</ListingCellLeft>,
        }
      );
    }

    if (showListedColumn) {
      cols.push({
        id: "listed",
        headerAlign: "right",
        align: "right",
        header: (
          <ListingHeaderContent onClick={() => handleSortClick("Listed")} style={{ cursor: "pointer" }}>
            Listed {sortArrow("Listed")}
          </ListingHeaderContent>
        ),
        render: (store) => (
          <ListingCellCenter>
            {store.listing_check_saved === true ? (
              <Check fontSize="small" sx={{ color: "#AF3241" }} />
            ) : (
              <Close fontSize="small" sx={{ color: "#AF3241" }} />
            )}
          </ListingCellCenter>
        ),
      });
    }

    return cols;
  }, [
    selectedStores,
    sortBy,
    sortDirection,
    showOwnerColumn,
    showListedColumn,
  ]);

  return (
    <>
      <ListingDataTableForModal
        columns={columns}
        rows={slicedFilteredStores}
        rowKey={(store) => store.id}
        emptyText="No stores available"
      />
    </>
  );
};

export default StoreDetailSelection;